import { z } from 'zod';

import { CONCERN_VALUES } from '../lib/concernEnum';

const concernEnum = z.enum(CONCERN_VALUES);

export const portalHelplineRoleEnum = z.enum(['practitioner', 'volunteer'] as const);
export type PortalHelplineRole = z.infer<typeof portalHelplineRoleEnum>;

/** Practitioner role values returned in callable list rows. Volunteers
 *  surface a `nickname`-derived `displayName` and have null first/last. */
const practitionerRoleEnum = z.enum(['psychologist', 'chief_psychologist', 'volunteer'] as const);

export const callablePractitionerSchema = z.object({
  practitionerId: z.string(),
  role: practitionerRoleEnum,
  displayName: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  // true ⟺ assigned at the location but not callable right now (off-shift, in
  // a call, or in cooldown). The picker greys these rows and hides the Call CTA.
  busy: z.boolean(),
  // When this student last completed a session with the practitioner (ISO),
  // null if never. The picker lifts the three most recent into "Your
  // psychologists". Nullish so a response from an older API still parses.
  lastSessionAt: z.string().nullish(),
});
export type CallablePractitioner = z.infer<typeof callablePractitionerSchema>;

export const availableNowPractitionerSchema = z.object({
  practitionerId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  blockId: z.string(),
  assignmentId: z.string(),
  locationIds: z.array(z.string()),
});
export type AvailableNowPractitioner = z.infer<typeof availableNowPractitionerSchema>;

export const startPortalCallRequestSchema = z.object({
  practitionerId: z.string().min(1).max(50),
  consentGiven: z.literal(true),
  concerns: z.array(concernEnum).optional(),
});
export type StartPortalCallRequest = z.infer<typeof startPortalCallRequestSchema>;

export const startPortalCallResponseSchema = z.object({
  id: z.string(),
  clientId: z.string(),
  practitionerId: z.string(),
  organizationId: z.string(),
  locationId: z.string(),
  sessionType: z.enum(['new', 'follow_up'] as const),
  sessionMode: z.literal('online'),
  projectKey: z.literal('portal_helpline'),
  status: z.literal('scheduled'),
  scheduledStart: z.string(),
  scheduledEnd: z.string(),
  durationMinutes: z.number(),
  concerns: z.array(concernEnum),
  consentGiven: z.literal(true),
  consentGivenAt: z.string(),
  createdAt: z.string(),
});
export type StartPortalCallResponse = z.infer<typeof startPortalCallResponseSchema>;
